import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Badge from "../../components/common/Badge";
import ActionButtons from "../../components/common/ActionButtons";
import { useStore } from "../../context/StoreContext";
import {
  useCreateTillMutation,
  useDeactivateTillMutation,
  useGetLocalDevicesQuery,
  useGetTillsQuery,
  useUpdateTillMutation,
} from "../../store/api";
import { getCashingUpStatusTone } from "../../utils/cashingUp";

const initialForm = {
  tillName: "",
  deviceId: "",
  defaultOpeningFloat: "0.00",
  blindCountEnabled: true,
  allowAnyCashier: true,
  isActive: true,
};

const getTillErrorMessage = (err, fallback) => {
  const code = err?.data?.code || err?.data?.errorCode || err?.data?.title;
  const errors = {
    TILL_NAME_EXISTS: "A till with this name already exists.",
    TILL_NAME_REQUIRED: "Till name is required.",
    TILL_NOT_FOUND: "Selected till could not be found.",
    TILL_INACTIVE: "This till is inactive and cannot be used.",
    STORE_SCOPE_MISMATCH: "You are viewing the wrong store scope.",
  };

  return (
    errors[code] ||
    err?.data?.detail ||
    err?.data?.message ||
    fallback ||
      "—"
  );
};

const buildFormFromTill = (till) => ({
  tillName: till?.tillName || "",
  deviceId: till?.deviceId || "",
  defaultOpeningFloat:
    till?.defaultOpeningFloat !== undefined &&
    till?.defaultOpeningFloat !== null
      ? String(till.defaultOpeningFloat)
      : "0.00",
  blindCountEnabled:
    till?.blindCountEnabled !== undefined ? till.blindCountEnabled : true,
  allowAnyCashier:
    till?.allowAnyCashier !== undefined ? till.allowAnyCashier : true,
  isActive: till?.isActive !== undefined ? till.isActive : true,
});

const Tills = () => {
  const { selectedStoreId } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [syncFilter, setSyncFilter] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [editingTillId, setEditingTillId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    tillId: null,
    tillName: "",
  });
  const [form, setForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [localError, setLocalError] = useState("");

  const {
    data: tillsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetTillsQuery({ storeId: selectedStoreId }, { skip: !selectedStoreId });
  const { data: localDevicesData } = useGetLocalDevicesQuery();

  const [createTill, createState] = useCreateTillMutation();
  const [updateTill, updateState] = useUpdateTillMutation();
  const [deactivateTill, deactivateState] = useDeactivateTillMutation();

  const tills = useMemo(
    () => (Array.isArray(tillsData) ? tillsData : []),
    [tillsData],
  );
  const localDevices = useMemo(
    () => (Array.isArray(localDevicesData) ? localDevicesData : []),
    [localDevicesData],
  );
  const deviceOptions = useMemo(
    () =>
      localDevices
        .filter(
          (device) =>
            !selectedStoreId || device.storeId === selectedStoreId,
        )
        .sort((a, b) =>
          String(a.deviceName || a.deviceKey || a.deviceId || "").localeCompare(
            String(b.deviceName || b.deviceKey || b.deviceId || ""),
          ),
        ),
    [localDevices, selectedStoreId],
  );
  const deviceById = useMemo(
    () =>
      new Map(deviceOptions.map((device) => [device.deviceId, device])),
    [deviceOptions],
  );
  const getDeviceLabel = (device) =>
    device?.deviceName || device?.deviceKey || device?.deviceId || "Unassigned";
  const filteredTills = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return tills.filter((till) => {
      const linkedDevice = deviceById.get(till.deviceId);
      const matchesQuery =
        !normalizedQuery ||
        [
          till.tillName,
          till.tillCode,
          till.deviceId,
          till.terminalId,
          till.storeId,
          till.syncStatus,
          linkedDevice?.deviceName,
          linkedDevice?.deviceKey,
          linkedDevice?.deviceType,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(normalizedQuery),
          );
      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && till.isActive) ||
        (statusFilter === "INACTIVE" && !till.isActive);
      const matchesSync =
        syncFilter === "ALL" ||
        String(till.syncStatus || "").toUpperCase() === syncFilter;
      return matchesQuery && matchesStatus && matchesSync;
    });
  }, [tills, query, statusFilter, syncFilter, deviceById]);
  useEffect(() => {
    if (!selectedStoreId) {
      setShowForm(false);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    const editTillId = location.state?.editTillId;
    if (!editTillId || tills.length === 0) {
      return;
    }

    const tillToEdit = tills.find((till) => till.tillId === editTillId);
    if (!tillToEdit) {
      return;
    }

    handleEdit(tillToEdit);
    navigate("/cash-management/tills", { replace: true, state: null });
  }, [location.state, navigate, tills]);

  const resetForm = () => {
    setEditingTillId(null);
    setForm(initialForm);
    setFormErrors({});
    setLocalError("");
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (till) => {
    setEditingTillId(till.tillId);
    setForm(buildFormFromTill(till));
    setFormErrors({});
    setLocalError("");
    setShowForm(true);
  };

  const handleView = (till) => {
    navigate(`/cash-management/tills/${till.tillId}`);
  };

  const handleDeactivate = (till) => {
    setDeleteModal({
      isOpen: true,
      tillId: till.tillId,
      tillName: till.tillName || "this till",
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormErrors({});
    setLocalError("");
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.tillName.trim()) {
      errors.tillName = "Till name is required.";
    }
    if (form.defaultOpeningFloat === "" || Number.isNaN(Number(form.defaultOpeningFloat))) {
      errors.defaultOpeningFloat = "Default opening float must be a number.";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      setLocalError("Please select or configure a store first.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      tillName: form.tillName.trim(),
      deviceId: form.deviceId.trim() || null,
      defaultOpeningFloat: Number(form.defaultOpeningFloat) || 0,
      blindCountEnabled: Boolean(form.blindCountEnabled),
      allowAnyCashier: Boolean(form.allowAnyCashier),
      isActive: Boolean(form.isActive),
    };

    try {
      if (editingTillId) {
        await updateTill({
          tillId: editingTillId,
          payload,
        }).unwrap();
      } else {
        await createTill({
          storeId: selectedStoreId,
          payload,
        }).unwrap();
      }
      setShowForm(false);
      resetForm();
    } catch (err) {
      setLocalError(getTillErrorMessage(err, "Failed to save till."));
    }
  };

  const confirmDeactivate = async () => {
    try {
      await deactivateTill(deleteModal.tillId).unwrap();
      setDeleteModal({ isOpen: false, tillId: null, tillName: "" });
      refetch();
    } catch (err) {
      setDeleteModal({ isOpen: false, tillId: null, tillName: "" });
      setLocalError(getTillErrorMessage(err, "Failed to deactivate till."));
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const columns = [
    { key: "tillName", label: "Till Name" },
    { key: "deviceId", label: "Linked Device" },
    { key: "status", label: "Status" },
    { key: "syncStatus", label: "Sync" },
    { key: "actions", label: "Actions" },
  ];

  const rows = filteredTills.map((till) => ({
    id: till.tillId,
    tillName: (
      <button
        type="button"
        onClick={() => handleView(till)}
        className="font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
      >
        {till.tillName || "Unnamed till"}
      </button>
    ),
    deviceId: getDeviceLabel(deviceById.get(till.deviceId)),
    status: (
      <Badge tone={till.isActive ? "success" : "neutral"}>
        {till.isActive ? "Active" : "Inactive"}
      </Badge>
    ),
    syncStatus: till.syncStatus ? (
      <Badge tone={getCashingUpStatusTone(till.syncStatus)}>
        {till.syncStatus}
      </Badge>
    ) : (
      "—"
    ),
    actions: (
      <ActionButtons
        actions={[
          {
            icon: "view",
            label: "View",
            color: "secondary",
            onClick: () => handleView(till),
          },
          {
            icon: "edit",
            label: "Edit",
            color: "primary",
            onClick: () => handleEdit(till),
          },
          {
            icon: "deactivate",
            label: "Deactivate",
            color: "danger",
            onClick: () => handleDeactivate(till),
          },
        ]}
      />
    ),
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tills"
        subtitle="Create, edit, deactivate, and review till setup for the selected store."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Refresh
            </Button>
            <Button size="sm" onClick={handleCreate} disabled={!selectedStoreId}>
              Add Till
            </Button>
          </div>
        }
      />

      {!selectedStoreId && (
        <Card>
          <Alert tone="warning">
            No store is currently selected. Till setup needs a store context
            before it can load or save records.
          </Alert>
        </Card>
      )}

      {selectedStoreId && (
        <div className="space-y-6">
          <Card className="space-y-4 p-0">
            <div className="px-6 pt-4">
              <div className="grid gap-3 xl:grid-cols-[minmax(280px,1.4fr)_180px_180px_180px]">
                <Input
                  label="Search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by till name, code, device, or terminal"
                />
                <div>
                  <label className="mb-2 block text-sm text-slate-600 dark:text-slate-400">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-slate-500"
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm text-slate-600 dark:text-slate-400">
                    Sync Status
                  </label>
                  <select
                    value={syncFilter}
                    onChange={(e) => setSyncFilter(e.target.value)}
                    className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-slate-500"
                  >
                    <option value="ALL">All Sync Status</option>
                    <option value="SYNCED">Synced</option>
                    <option value="PENDING">Pending</option>
                    <option value="NOT_SYNCED">Not Synced</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setQuery("");
                      setStatusFilter("ALL");
                      setSyncFilter("ALL");
                    }}
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>
            </div>

          <div className="px-6 pb-6 pt-4">
              {isLoading && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Loading tills...
                </p>
              )}
              {isError && (
                <Alert tone="error">
                  {getTillErrorMessage(error, "Failed to load tills.")}
                </Alert>
              )}
              {localError && <Alert tone="error">{localError}</Alert>}
              {!isLoading && !isError && (
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No tills found for the selected filters."
                />
              )}
            </div>
          </Card>
        </div>
      )}

      <Modal
        isOpen={showForm}
        onClose={cancelForm}
        title={editingTillId ? "Edit Till" : "Create Till"}
        confirmText={editingTillId ? "Update" : "Create"}
        onConfirm={handleSubmit}
        size="xl"
        isLoading={createState.isLoading || updateState.isLoading}
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-1">
            <Input
              label="Till Name"
              name="tillName"
              value={form.tillName}
              onChange={handleChange}
              error={formErrors.tillName}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Linked Device
              </label>
              <select
                name="deviceId"
                value={form.deviceId}
                onChange={handleChange}
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-slate-500"
              >
                <option value="">Unassigned</option>
                {deviceOptions.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {getDeviceLabel(device)}
                    {device.deviceType ? ` (${device.deviceType})` : ""}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Select a device from the current store instead of pasting an ID.
              </p>
            </div>
            <Input
              label="Terminal ID"
              name="terminalId"
              value={form.terminalId}
              onChange={handleChange}
              helper="Optional payment terminal assignment."
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Checkbox
              label="Blind Count Enabled"
              name="blindCountEnabled"
              checked={form.blindCountEnabled}
              onChange={handleChange}
            />
            <Checkbox
              label="Allow Any Cashier"
              name="allowAnyCashier"
              checked={form.allowAnyCashier}
              onChange={handleChange}
            />
            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />
          </div>

          {(createState.isError || updateState.isError) && (
            <Alert tone="error">
              {getTillErrorMessage(
                createState.error || updateState.error,
                "Failed to save till.",
              )}
            </Alert>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, tillId: null, tillName: "" })
        }
        title="Deactivate Till"
        confirmText="Deactivate"
        confirmVariant="primary"
        onConfirm={confirmDeactivate}
        isLoading={deactivateState.isLoading}
      >
        <p>
          Are you sure you want to deactivate <strong>{deleteModal.tillName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This till will no longer be available for opening new sessions.
        </p>
      </Modal>
    </div>
  );
};

export default Tills;



